import { alpha, type Theme } from '@mui/material/styles'
import type { SystemStyleObject } from '@mui/system'

/**
 * Высота строки поля редактора: из неё считается минимальная высота поля
 * (`rows`) и зона клика под последним блоком.
 */
export const EDITOR_JS_ROW_HEIGHT_PX = 28

/**
 * Поле справа под панель блока (плюс и шестерёнка) — столько же, сколько
 * Editor.js отводит сам в узком режиме.
 */
const EDITOR_JS_TOOLBAR_GUTTER_PX = 50

/**
 * Настольный вид Editor.js (его CSS переключается на 650px). На узком экране
 * панель блока встаёт под блоком, и поле под неё не нужно.
 */
const EDITOR_JS_DESKTOP_MEDIA = '@media (min-width: 651px)'

/** Минимальная ширина колонки таблицы в режиме чтения */
const EDITOR_JS_TABLE_MIN_COLUMN_REM = 7

/** Сколько колонок таблицы покрывают правила минимальной ширины */
const EDITOR_JS_TABLE_MAX_COLUMNS = 20

/**
 * Минимальная ширина таблицы только для чтения — по числу колонок.
 *
 * Колонки таблицы Editor.js делят ширину поровну с минимумом 10px, и на
 * телефоне пять колонок сжимаются так, что слова рвутся по букве. Числа
 * колонок в разметке нет, поэтому оно берётся из ячеек через `:has` (строки
 * таблицы одинаковой длины): при нескольких совпадениях побеждает последнее
 * правило — с наибольшим числом колонок. Таблица шире поля листается внутри
 * него.
 *
 * `nth-of-type`, а не `nth-child`: Emotion ругается на `*-child` в консоли
 * (небезопасно при серверном рендере), а ячейки и так одни `div` в строке.
 */
function readOnlyTableMinWidthSx(): SystemStyleObject<Theme> {
  const rules: Record<string, SystemStyleObject<Theme>> = {}

  for (let columns = 2; columns <= EDITOR_JS_TABLE_MAX_COLUMNS; columns += 1) {
    const selector = `& .codex-editor .tc-wrap--readonly .tc-table:has(.tc-cell:nth-of-type(${columns}))`
    rules[selector] = { minWidth: `${columns * EDITOR_JS_TABLE_MIN_COLUMN_REM}rem` }
  }

  return rules
}

/**
 * `content` — просмотр материала, `field` — поле ввода (в том числе временно
 * заблокированное).
 */
export type EditorJsLayout = 'content' | 'field'

/**
 * Раскладка Editor.js внутри холдера — для обоих режимов и обеих тем.
 *
 * Editor.js свёрстан под отдельную страницу-редактор, а не под поле в
 * колонке:
 * - под последним блоком он всегда, и в режиме чтения тоже, держит пустую
 *   зону `minHeight` (по умолчанию 300px) — inline-стилем, поэтому перебить
 *   её можно только `!important`;
 * - блок и панель ограничены 650px и центрированы — в широкой колонке по
 *   бокам пустые поля;
 * - если холдер меньше 650px в ширину, справа резервируется 50px под панель
 *   блока — и в режиме чтения тоже.
 *
 * Отсюда «пустое место» под теорией в уроке при любом её объёме.
 *
 * `content`: высота по содержимому, текст на всю ширину колонки, без зоны
 * клика и без поля под панель — панели в режиме чтения нет.
 *
 * `field`: редактор растянут на всю высоту поля (клик по пустому месту
 * попадает в зону редактора, и Editor.js ставит курсор в последний блок или
 * добавляет новый), под последним блоком — зона клика в одну строку, справа —
 * поле под панель блока при любой ширине. Панель при этом всегда справа:
 * со снятым ограничением 650px её обычное место слева от блока оказалось бы
 * за границей поля.
 *
 * Селекторы с лишним `.codex-editor`: стили Editor.js подключаются в `head`
 * позже стилей MUI, и при равной специфичности победили бы они.
 */
export function editorJsLayoutSx(layout: EditorJsLayout): SystemStyleObject<Theme> {
  const shared: SystemStyleObject<Theme> = {
    '& .codex-editor .ce-block__content, & .codex-editor .ce-toolbar__content': {
      maxWidth: 'none',
    },
    // Длинная ссылка без пробелов иначе уходит за рамку поля
    '& .codex-editor .ce-block__content': {
      overflowWrap: 'anywhere',
    },
  }

  if (layout === 'content') {
    return {
      ...shared,
      '& .codex-editor .codex-editor__redactor': {
        paddingBottom: '0 !important',
        marginRight: 0,
      },
      // Цитата и предупреждение резервируют место под ввод (158px и 85px): в
      // режиме чтения это та же пустота под текстом
      '& .codex-editor .cdx-quote__text, & .codex-editor .cdx-warning__message': {
        minHeight: 0,
      },
      // Таблица только для чтения держит справа пустую колонку под кнопку
      // «добавить столбец» и вылезает за поле на её ширину. Широкая таблица
      // на телефоне листается внутри поля, а не сжимает текст по букве в
      // колонке. `contain` — чтобы минимальная ширина таблицы не расталкивала
      // карточку и страницу: ширину обёртке задаёт колонка, а не содержимое.
      '& .codex-editor .tc-wrap--readonly': {
        gridTemplateColumns: '100%',
        overflowX: 'auto',
        contain: 'inline-size',
      },
      ...readOnlyTableMinWidthSx(),
    }
  }

  return {
    ...shared,
    display: 'flex',
    flexDirection: 'column',
    '& > .codex-editor': {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    '& .codex-editor .codex-editor__redactor': {
      flexGrow: 1,
      paddingBottom: `${EDITOR_JS_ROW_HEIGHT_PX}px !important`,
    },
    [EDITOR_JS_DESKTOP_MEDIA]: {
      '& .codex-editor .codex-editor__redactor': {
        marginRight: `${EDITOR_JS_TOOLBAR_GUTTER_PX}px`,
      },
      '& .codex-editor .ce-toolbar__actions': {
        right: '-5px',
      },
    },
  }
}

/**
 * Переопределения стилей Editor.js под тёмную тему.
 *
 * Своей тёмной темы у Editor.js нет: он рисует управление тёмным по светлому и
 * в тёмной теме кнопки плюса и настроек блока сливаются с фоном — учитель
 * находил их только наведением, когда всплывала подложка.
 *
 * Правила живут отдельным модулем и вешаются на холдер редактора через `sx`, а
 * не в глобальный CSS: так цвета берутся из токенов темы MUI, а не хардкодятся,
 * и переопределения не протекают на страницы, где Editor.js не смонтирован.
 * Селекторы Editor.js 2.31: панель блока и её попап рендерятся внутри холдера,
 * поэтому потомковых селекторов достаточно.
 *
 * В светлой теме возвращается пустой объект — её внешний вид не меняется.
 */
export function editorJsThemeSx(theme: Theme): SystemStyleObject<Theme> {
  if (theme.palette.mode !== 'dark') {
    return {}
  }

  const surface = theme.palette.background.paper
  const hovered = alpha(theme.palette.common.white, 0.08)
  const border = theme.palette.divider

  return {
    // Плюс и шестерёнка: тёмные по умолчанию, поэтому в тёмной теме их не было
    // видно без наведения.
    '& .ce-toolbar__plus, & .ce-toolbar__settings-btn': {
      color: theme.palette.text.primary,
      backgroundColor: 'transparent',
      border: `1px solid ${border}`,
    },
    '& .ce-toolbar__plus:hover, & .ce-toolbar__settings-btn:hover': {
      backgroundColor: hovered,
      color: theme.palette.text.primary,
    },

    // Меню выбора блока и меню настроек блока — один компонент попапа.
    '& .ce-popover, & .ce-popover__container': {
      backgroundColor: surface,
      borderColor: border,
      color: theme.palette.text.primary,
      boxShadow: theme.shadows[8],
    },
    '& .ce-popover-item__title, & .ce-popover-item__icon, & .ce-popover__items': {
      color: theme.palette.text.primary,
    },
    '& .ce-popover-item__icon': {
      backgroundColor: alpha(theme.palette.common.white, 0.06),
      boxShadow: 'none',
    },
    '& .ce-popover-item:hover:not(.ce-popover-item--no-hover)': {
      backgroundColor: hovered,
    },
    '& .ce-popover-item--active': {
      backgroundColor: alpha(theme.palette.primary.main, 0.24),
      color: theme.palette.primary.contrastText,
    },
    // Поиск по блокам внутри попапа: поле ввода тоже светлое по умолчанию.
    '& .cdx-search-field': {
      backgroundColor: alpha(theme.palette.common.white, 0.06),
      borderColor: border,
      color: theme.palette.text.primary,
    },
    '& .cdx-search-field__input': {
      color: theme.palette.text.primary,
      '&::placeholder': { color: theme.palette.text.disabled },
    },
    '& .cdx-search-field__icon svg': {
      color: theme.palette.text.secondary,
    },

    // Инлайн-панель форматирования выделенного текста.
    '& .ce-inline-toolbar': {
      backgroundColor: surface,
      borderColor: border,
      color: theme.palette.text.primary,
      boxShadow: theme.shadows[8],
    },
    '& .ce-inline-tool, & .ce-inline-toolbar__dropdown': {
      color: theme.palette.text.primary,
    },
    '& .ce-inline-tool:hover, & .ce-inline-toolbar__dropdown:hover': {
      backgroundColor: hovered,
    },
    '& .ce-inline-tool--active': {
      color: theme.palette.primary.light,
    },
    // Поле ссылки инлайн-панели.
    '& .ce-inline-tool-input': {
      backgroundColor: alpha(theme.palette.common.white, 0.06),
      color: theme.palette.text.primary,
      '&::placeholder': { color: theme.palette.text.disabled },
    },

    // Подсказка пустого блока: дефолтная слишком тёмная на тёмном фоне.
    '& .ce-paragraph[data-placeholder]:empty::before, & .ce-header[data-placeholder]:empty::before': {
      color: theme.palette.text.disabled,
    },

    // Выделение блока при переносе и при выборе — светлая заливка по умолчанию
    // делает текст нечитаемым.
    '& .ce-block--selected .ce-block__content': {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
    },
    '& .ce-toolbar__settings-btn--hidden': {
      opacity: 0,
    },

    // Таблица: строка заголовка по умолчанию на почти белой подложке, и
    // светлый текст тёмной темы на ней не виден; рамки ячеек тоже светлые.
    '& .tc-wrap': {
      '--color-background': alpha(theme.palette.common.white, 0.06),
      '--color-border': border,
      '--color-text-secondary': theme.palette.text.secondary,
    },
    // Меню строки и столбца таблицы — белое по умолчанию.
    '& .tc-popover': {
      '--color-background': surface,
      '--color-border': border,
      '--color-background-hover': hovered,
      color: theme.palette.text.primary,
    },
  }
}
